import { useState } from "react";
import type { AutomationRules as AutomationRulesType } from "@type/agent";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { Input } from "@component/input";
import { cn } from "@util/tailwind";

interface AutomationRulesProps {
    rules: AutomationRulesType;
    onChange: (rules: AutomationRulesType) => void;
    disabled?: boolean;
}

type TimingMode = "immediate" | "scheduled";

export function AutomationRulesEditor({ rules, onChange, disabled }: AutomationRulesProps) {
    const handleChange = (updates: Partial<AutomationRulesType>) => {
        onChange({ ...rules, ...updates });
    };

    const raw = rules.scheduleConfig as Record<string, unknown> | undefined;
    const timingMode = (raw?.timingMode as TimingMode) || "immediate";
    const scheduledDate = (raw?.scheduledDate as string) || "";
    const scheduledTime = (raw?.scheduledTime as string) || "";

    const setTimingMode = (mode: TimingMode) => {
        handleChange({
            scheduleConfig: {
                ...raw,
                timingMode: mode,
            } as unknown as AutomationRulesType["scheduleConfig"],
        });
    };

    const setScheduledDate = (date: string) => {
        handleChange({
            scheduleConfig: {
                ...raw,
                timingMode: "scheduled",
                scheduledDate: date,
            } as unknown as AutomationRulesType["scheduleConfig"],
        });
    };

    const setScheduledTime = (time: string) => {
        handleChange({
            scheduleConfig: {
                ...raw,
                timingMode: "scheduled",
                scheduledTime: time,
            } as unknown as AutomationRulesType["scheduleConfig"],
        });
    };

    // Validate minimum 60 minutes from now
    const now = new Date();
    const minDate = now.toISOString().split("T")[0];
    let dateTimeWarning = "";
    if (timingMode === "scheduled" && scheduledDate && scheduledTime) {
        const scheduled = new Date(`${scheduledDate}T${scheduledTime}`);
        const diffMs = scheduled.getTime() - now.getTime();
        if (diffMs < 60 * 60 * 1000) {
            dateTimeWarning = "Scheduled time must be at least 60 minutes from now.";
        }
    }

    return (
        <div className={disabled ? "space-y-6 pointer-events-none opacity-60" : "space-y-6"}>
            {/* Rules */}
            <Card>
                <CardHeader><CardTitle>Rules</CardTitle></CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Retry Attempts</label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={3}
                                    value={rules.retryAttempts.toString()}
                                    onChange={(e) => {
                                        const val = Math.min(3, Math.max(1, parseInt(e.target.value) || 1));
                                        handleChange({ retryAttempts: val });
                                    }}
                                />
                                <p className="text-xs text-gray-500 mt-1">1–3 retry attempts if call fails.</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Retry Delay (minutes)</label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={60}
                                    value={rules.retryDelayMinutes.toString()}
                                    onChange={(e) => {
                                        const val = Math.min(60, Math.max(0, parseInt(e.target.value) || 0));
                                        handleChange({ retryDelayMinutes: val });
                                    }}
                                />
                                <p className="text-xs text-gray-500 mt-1">Adds a delay (in minutes) between each retry attempt.</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Timezone */}
            <Card>
                <CardHeader><CardTitle>Timezone</CardTitle></CardHeader>
                <CardContent>
                    <div className="h-9 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm flex items-center text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                        Asia/Kuala_Lumpur (MYT)
                    </div>
                </CardContent>
            </Card>

            {/* Timing */}
            <Card>
                <CardHeader><CardTitle>Timing</CardTitle></CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {/* Sub-tabs */}
                        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setTimingMode("immediate")}
                                className={cn(
                                    "flex-1 px-4 py-2.5 text-sm font-medium transition-colors",
                                    timingMode === "immediate"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-600 hover:bg-gray-50"
                                )}
                            >
                                Send immediately
                            </button>
                            <button
                                onClick={() => setTimingMode("scheduled")}
                                className={cn(
                                    "flex-1 px-4 py-2.5 text-sm font-medium transition-colors border-l border-gray-200",
                                    timingMode === "scheduled"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white text-gray-600 hover:bg-gray-50"
                                )}
                            >
                                Schedule for later
                            </button>
                        </div>

                        {timingMode === "immediate" && (
                            <div className="py-8 text-center">
                                <p className="text-sm text-gray-500">Campaign will start calling when saved and activated.</p>
                            </div>
                        )}

                        {timingMode === "scheduled" && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Start Date</label>
                                        <Input
                                            type="date"
                                            value={scheduledDate}
                                            min={minDate}
                                            onChange={(e) => setScheduledDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Start Time</label>
                                        <Input
                                            type="time"
                                            value={scheduledTime}
                                            onChange={(e) => setScheduledTime(e.target.value)}
                                        />
                                    </div>
                                </div>
                                {dateTimeWarning && (
                                    <p className="text-xs text-red-600">{dateTimeWarning}</p>
                                )}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Timezone</label>
                                    <div className="h-9 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm flex items-center text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                                        Asia/Kuala_Lumpur (MYT)
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
